import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import Logo from "../components/Logo";
import { Camera, CloudSun, BookOpen, Bug, ChevronRight, Mail, Lock, User as UserIcon, Globe } from "lucide-react";
import { useAuth, setStoredToken } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setUser, checkAuth } = useAuth();

  const [mode, setMode] = useState("login"); // "login" | "register"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [country, setCountry] = useState("IT");
  const [loading, setLoading] = useState(false);

  const [googleEnabled, setGoogleEnabled] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [signingIn, setSigningIn] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/auth/config`)
      .then((r) => setGoogleEnabled(Boolean(r.data?.google_enabled)))
      .catch(() => setGoogleEnabled(false))
      .finally(() => setConfigLoaded(true));
  }, []);

  // Se il login Google e' fallito, il backend ci rimanda qui con ?auth_error=...
  useEffect(() => {
    const reason = new URLSearchParams(window.location.search).get("auth_error");
    if (!reason) return;
    const messaggi = {
      google_denied: "Accesso Google annullato.",
      expired_state: "La richiesta di accesso è scaduta. Riprova.",
      email_not_verified: "L'indirizzo Google non risulta verificato.",
    };
    toast.error(messaggi[reason] || "Accesso con Google non riuscito. Riprova.");
    window.history.replaceState(null, "", window.location.pathname);
  }, []);

  // Redirect a pagina intera verso Google, gestito dal backend.
  // Niente libreria JS e niente popup: e' l'unico flusso che regge su iPhone,
  // dove WebKit blocca lo storage di terze parti usato da Google Identity Services.
  const handleGoogleLogin = () => {
    setSigningIn(true);
    const redirect = encodeURIComponent(window.location.origin);
    window.location.href = `${API}/auth/google/login?redirect=${redirect}`;
  };

  const handleForgotPassword = async () => {
    if (!email.trim()) {
      toast.error(t("login.errors.emailRequiredForReset"));
      return;
    }
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      toast.success(res.data?.message || t("login.forgotPasswordSuccess"));
    } catch (e) {
      toast.error(t("login.errors.generic"));
    }
  };

  const handleEmailAuth = async () => {
    if (!email.trim() || !password) {
      toast.error(t("login.errors.missingCredentials"));
      return;
    }
    if (mode === "register" && password.length < 8) {
      toast.error(t("login.errors.passwordTooShort"));
      return;
    }
    setLoading(true);
    try {
      const endpoint = mode === "register" ? "/auth/register" : "/auth/login";
      const body =
        mode === "register"
          ? { email: email.trim(), password, name: name.trim(), country }
          : { email: email.trim(), password };
      const res = await axios.post(`${API}${endpoint}`, body, { withCredentials: true });

      if (res.data?.session_token) setStoredToken(res.data.session_token);
      setUser(res.data);
      // /auth/register e /auth/login non restituiscono il country: lo recuperiamo
      // via /auth/me (checkAuth) cosi' userCountryLang e' gia' corretto prima del redirect.
      await checkAuth();

      if (mode === "register" && res.data?.is_new_user && typeof window.gtag === "function") {
        window.gtag("event", "sign_up", { method: "email" });
        window.gtag("event", "conversion", {
          send_to: "AW-18183121570/k-diCL2_jcEcEKLVsd5D",
          value: 1.0,
          currency: "EUR",
          transaction_id: res.data?.user_id || `signup-${Date.now()}`,
        });
      }

      navigate("/dashboard", { replace: true });
    } catch (e) {
      const msg = e?.response?.data?.detail || t("login.errors.generic");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: CloudSun, title: t("login.features.weather.title"), desc: t("login.features.weather.desc") },
    { icon: Bug, title: t("login.features.diseaseAlerts.title"), desc: t("login.features.diseaseAlerts.desc") },
    { icon: BookOpen, title: t("login.features.treatments.title"), desc: t("login.features.treatments.desc") },
    { icon: Camera, title: t("login.features.diagnosis.title"), desc: t("login.features.diagnosis.desc") },
  ];

  return (
    <div className="min-h-screen" data-testid="login-page" style={{ background: "var(--bg)" }}>
      <div className="vs-gradient-bar" />
      <div className="max-w-2xl mx-auto px-5 pt-8 pb-12">
        {/* Brand */}
        <div className="flex items-center gap-3 mb-6 vs-fade-up">
          <Logo size={42} />
          <div>
            <div className="heading text-xl font-extrabold tracking-tight" style={{ color: "var(--text-primary)" }}>
              Il Mio Vigneto
            </div>
            <div className="text-xs font-semibold uppercase tracking-[0.15em]" style={{ color: "var(--grape)" }}>
              {t("login.tagline")}
            </div>
          </div>
        </div>

        {/* Hero photo */}
        <div className="vs-hero-photo vs-fade-up mb-6" style={{ animationDelay: "80ms" }}>
          <img
            src="https://images.pexels.com/photos/760281/pexels-photo-760281.jpeg?auto=compress&cs=tinysrgb&w=1200"
            alt={t("login.heroImageAlt")}
          />
          <span className="badge">{t("common.heroTagline")}</span>
        </div>

        {/* Hero text */}
        <div className="vs-fade-up" style={{ animationDelay: "140ms" }}>
          <h1
            className="heading font-extrabold tracking-tight leading-[1.05] mb-4"
            style={{ fontSize: "clamp(2rem, 7vw, 3rem)", color: "var(--text-primary)" }}
          >
            {t("login.heroTitleLine1")} <span style={{ color: "var(--primary)" }}>{t("login.heroTitleHighlight1")}</span><br />
            <span style={{ color: "var(--grape)" }}>{t("login.heroTitleHighlight2")}</span>
          </h1>
          <p className="text-base mb-2" style={{ color: "var(--text-secondary)", maxWidth: 560 }}>
            {t("login.heroDescription")}
          </p>
          <p className="text-sm font-semibold mb-7" style={{ color: "var(--primary)" }}>
            {t("login.heroTrialCta")}
          </p>

          {/* Google Sign-In (self-managed, own Client ID -> consent screen shows "Il Mio Vigneto") */}
          <div data-testid="google-login-wrapper" className="flex flex-col items-stretch">
            {!configLoaded && (
              <div className="vs-spinner mx-auto" data-testid="login-config-loading" />
            )}
            {configLoaded && googleEnabled && (
              <button
                onClick={handleGoogleLogin}
                disabled={signingIn}
                className="vs-btn-primary w-full inline-flex items-center justify-center gap-3 px-7"
                style={{ background: "var(--gradient-vine)", border: "2px solid var(--primary)", opacity: signingIn ? 0.6 : 1 }}
                data-testid="google-login-btn"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="#FFFFFF" d="M21.6 12.227c0-.788-.071-1.546-.205-2.273H12v4.302h5.385c-.232 1.252-.94 2.313-2.005 3.025v2.515h3.243c1.898-1.747 2.977-4.32 2.977-7.569z"/>
                  <path fill="#FFFFFF" d="M12 22c2.7 0 4.964-.895 6.62-2.422l-3.243-2.515c-.898.602-2.046.96-3.377.96-2.6 0-4.804-1.755-5.591-4.115H3.054v2.59A9.998 9.998 0 0012 22z" opacity=".85"/>
                  <path fill="#FFFFFF" d="M6.41 13.908a6.013 6.013 0 010-3.815v-2.59H3.054a9.998 9.998 0 000 8.995l3.357-2.59z" opacity=".7"/>
                  <path fill="#FFFFFF" d="M12 5.978c1.467 0 2.785.504 3.821 1.494l2.866-2.866C16.96 3.034 14.696 2 12 2A9.998 9.998 0 003.054 7.503l3.357 2.59C7.196 7.733 9.4 5.978 12 5.978z" opacity=".55"/>
                </svg>
                {t("login.googleButton")}
                <ChevronRight size={18} />
              </button>
            )}
            {configLoaded && !googleEnabled && (
              <div
                className="vs-card p-4 text-sm"
                data-testid="google-login-unconfigured"
                style={{ background: "#FEF3C7", color: "#78350F" }}
              >
                <strong>Login non ancora configurato.</strong> L'amministratore deve
                impostare le variabili <code>GOOGLE_CLIENT_ID</code> e{" "}
                <code>GOOGLE_CLIENT_SECRET</code> sul server.
              </div>
            )}
            {signingIn && (
              <p className="mt-3 text-xs text-center" style={{ color: "var(--text-muted)" }} data-testid="login-signing-in">
                Accesso in corso...
              </p>
            )}
          </div>

          <p className="mt-3 text-xs flex items-center gap-1.5" style={{ color: "var(--text-muted)" }}>
            <span className="vs-grape-dot" /> {t("login.badgeFree")} <span className="vs-grape-dot" /> {t("login.badgeSecureLogin")} <span className="vs-grape-dot" /> {t("login.badgePrivateData")}
          </p>

          <div
            className="mt-4 px-4 py-2.5 rounded-lg text-sm font-semibold text-center"
            style={{ background: "var(--secondary)", color: "var(--primary)", border: "1.5px solid var(--primary)" }}
            data-testid="trial-reassurance-banner"
          >
            {t("login.trialReassurance")}
          </div>

          {/* Separatore */}
          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px" style={{ background: "var(--border, #ddd)" }} />
            <span className="text-xs font-semibold" style={{ color: "var(--text-muted)" }}>{t("login.orSeparator")}</span>
            <div className="flex-1 h-px" style={{ background: "var(--border, #ddd)" }} />
          </div>

          {/* Form email/password */}
          <div className="vs-card p-5" data-testid="email-auth-form">
            {/* Tab switch */}
            <div className="flex gap-2 mb-4">
              <button
                type="button"
                onClick={() => setMode("login")}
                className="flex-1 py-2 rounded-lg text-sm font-semibold transition"
                style={{
                  background: mode === "login" ? "var(--primary)" : "transparent",
                  color: mode === "login" ? "#fff" : "var(--text-secondary)",
                  border: "1px solid var(--primary)",
                }}
                data-testid="tab-login"
              >
                {t("login.tabLogin")}
              </button>
              <button
                type="button"
                onClick={() => setMode("register")}
                className="flex-1 py-2 rounded-lg text-sm font-semibold transition"
                style={{
                  background: mode === "register" ? "var(--primary)" : "transparent",
                  color: mode === "register" ? "#fff" : "var(--text-secondary)",
                  border: "1px solid var(--primary)",
                }}
                data-testid="tab-register"
              >
                {t("login.tabRegister")}
              </button>
            </div>

            {mode === "register" && (
              <div className="mb-3">
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg" style={{ border: "1px solid var(--border, #ddd)", background: "var(--bg)" }}>
                  <UserIcon size={18} style={{ color: "var(--text-muted)" }} />
                  <input
                    type="text"
                    placeholder={t("login.namePlaceholder")}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="flex-1 bg-transparent outline-none text-sm"
                    style={{ color: "var(--text-primary)" }}
                    data-testid="input-name"
                  />
                </div>
              </div>
            )}

            {mode === "register" && (
              <div className="mb-3">
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg" style={{ border: "1px solid var(--border, #ddd)", background: "var(--bg)" }}>
                  <Globe size={18} style={{ color: "var(--text-muted)" }} />
                  <select
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="flex-1 bg-transparent outline-none text-sm"
                    style={{ color: "var(--text-primary)" }}
                    data-testid="input-country"
                  >
                    <option value="IT">{t("login.countryIt")}</option>
                    <option value="FR">{t("login.countryFr")}</option>
                  </select>
                </div>
                <p className="text-xs mt-1.5 px-1" style={{ color: "var(--text-muted)" }}>
                  {t("login.countryHelperText")}
                </p>
              </div>
            )}

            <div className="mb-3">
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg" style={{ border: "1px solid var(--border, #ddd)", background: "var(--bg)" }}>
                <Mail size={18} style={{ color: "var(--text-muted)" }} />
                <input
                  type="email"
                  placeholder={t("login.emailPlaceholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 bg-transparent outline-none text-sm"
                  style={{ color: "var(--text-primary)" }}
                  data-testid="input-email"
                />
              </div>
            </div>

            <div className="mb-4">
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg" style={{ border: "1px solid var(--border, #ddd)", background: "var(--bg)" }}>
                <Lock size={18} style={{ color: "var(--text-muted)" }} />
                <input
                  type="password"
                  placeholder={mode === "register" ? t("login.passwordPlaceholderRegister") : t("login.passwordPlaceholderLogin")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleEmailAuth(); }}
                  className="flex-1 bg-transparent outline-none text-sm"
                  style={{ color: "var(--text-primary)" }}
                  data-testid="input-password"
                />
              </div>
            </div>

            <button
              onClick={handleEmailAuth}
              disabled={loading}
              className="vs-btn-primary w-full inline-flex items-center justify-center gap-2"
              style={{ background: "var(--grape)", border: "2px solid var(--grape)", opacity: loading ? 0.6 : 1 }}
              data-testid="email-auth-btn"
            >
              {loading ? t("login.submitLoading") : mode === "register" ? t("login.submitRegister") : t("login.submitLogin")}
            </button>
            {mode === "login" && (
              <button
                type="button"
                onClick={handleForgotPassword}
                className="w-full text-center text-xs mt-3 underline"
                style={{ color: "var(--text-muted)" }}
                data-testid="forgot-password-link"
              >
                {t("login.forgotPassword")}
              </button>
            )}
          </div>
        </div>

        {/* Features grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-10">
          {features.map((f, i) => {
            const Icon = f.icon;
            const colors = [
              { bg: "#E4F0E6", fg: "var(--primary)" },
              { bg: "#FAE6D8", fg: "var(--accent)" },
              { bg: "#F5E8DA", fg: "#8A5A1C" },
              { bg: "#EFE0EB", fg: "var(--grape)" },
            ][i];
            return (
              <div
                key={i}
                className="vs-card p-4 flex items-start gap-3 vs-fade-up"
                style={{ animationDelay: `${220 + i * 80}ms` }}
                data-testid={`feature-${i}`}
              >
                <div
                  className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center"
                  style={{ background: colors.bg, border: `2px solid ${colors.fg}` }}
                >
                  <Icon size={22} style={{ color: colors.fg }} strokeWidth={2.2} />
                </div>
                <div>
                  <div className="font-semibold text-[15px]" style={{ color: "var(--text-primary)" }}>
                    {f.title}
                  </div>
                  <div className="text-sm" style={{ color: "var(--text-muted)" }}>
                    {f.desc}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div
          className="mt-10 p-4 vs-card flex items-center gap-3"
          style={{ background: "var(--secondary)" }}
        >
          <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--gold)" }}>
            <span className="text-lg">💡</span>
          </div>
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
            <strong>{t("login.tipLabel")}</strong> {t("login.tipText")}
          </p>
        </div>
      </div>
    </div>
  );
}
